/* eslint-disable sonarjs/no-duplicate-string */
import { Tooltip, TooltipContent, TooltipTrigger } from '@landler/component-library';
import { CellContext, Column, ColumnDef, ColumnDefResolved } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { IndicatorFactType, IndicatorName } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { useAboveGroundCarbonFacts } from '@/hooks/useAboveGroundCarbonFacts';
import { useHideTrendFacts } from '@/hooks/useHideTrendFacts';
import { useIndicatorProduct } from '@/hooks/useIndicatorProduct';
import {
  AreaCell,
  BiodiversityZoneCell,
  BiodiversityZoneTrendCell,
  CarbonStorageAgCell,
  CarbonStorageAgTrendCell,
  CarbonStorageBgCell,
  CarbonStorageBgTrendCell,
  LabelsCell,
  PlotRow,
  PlotTypeCell,
  WaterHoldingCapacityCell,
  WaterHoldingCapacityTrendCell,
} from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plots/components/Cells';
import { isNullish } from '@/utils/booleans';

import {
  EcologicalIntegrityIndexCell,
  FactCell,
  FactTrendCell,
  MonetaryRiskCell,
} from '../../projects/pages/project/pages/sustainableProduction/components/plot-list/Cells';
import { ProjectTypeAccessorKey } from '../../projects/pages/project/pages/sustainableProduction/components/plot-list/constants';
import { ToggleSortButton } from '../../projects/pages/project/pages/sustainableProduction/components/plot-list/ToggleSortButton';

export type MetadataColumn = ColumnDef<PlotRow> & Pick<ColumnDefResolved<PlotRow>, 'accessorKey'>;

type CellHeader = {
  column: Column<PlotRow, unknown>;
};

const HEADER_TRANSLATION_KEY = 'shared.plots.tableHeader.size';

export const useOrderedCustomCells = (customCells: MetadataColumn[], accessorKeyOrder: string[]) => {
  return customCells.sort((a, b) => {
    const aOrder = accessorKeyOrder.findIndex((item) => item === (a?.accessorKey as string)) ?? 0;
    const bOrder = accessorKeyOrder.findIndex((item) => item === (b?.accessorKey as string)) ?? 0;
    // descending as default to support lesser overrides
    return bOrder - aOrder;
  });
};

export const useDefaultCustomCells = () => {
  const { t } = useTranslation();
  return [
    {
      accessorKey: 'type',
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.plots.tableHeader.type')}
        </ToggleSortButton>
      ),
      cell: PlotTypeCell,
    },
    {
      accessorKey: 'area',
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t(HEADER_TRANSLATION_KEY)}
        </ToggleSortButton>
      ),
      cell: AreaCell,
    },
    {
      accessorKey: ProjectTypeAccessorKey.labels,
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.plots.tableHeader.labels')}
        </ToggleSortButton>
      ),
      meta: {
        styles: {
          cell: 'py-2',
        },
      },
      cell: LabelsCell,
    },
  ];
};

export const useGetCustomCells = () => {
  const { t } = useTranslation();

  return [
    ...useDefaultCustomCells(),
    {
      accessorKey: 'monetaryRisk',
      header: ({ column }: CellHeader) => (
        <Tooltip>
          <TooltipTrigger asChild className='uppercase'>
            <ToggleSortButton column={column} className='-ml-2.5'>
              {t('global.widget.stewardedValue')}
            </ToggleSortButton>
          </TooltipTrigger>
          <TooltipContent sideOffset={10} side='top' align='center'>
            {t('global.widget.stewardedValueTooltip')}
          </TooltipContent>
        </Tooltip>
      ),
      cell: MonetaryRiskCell,
    },
    {
      accessorKey: ProjectTypeAccessorKey.eii,
      header: ({ column }: CellHeader) => (
        <Tooltip>
          <TooltipTrigger asChild className='uppercase'>
            <ToggleSortButton column={column} className='-ml-2.5'>
              {t('global.widget.ecologicalIntegrityIndex.title')}
            </ToggleSortButton>
          </TooltipTrigger>
          <TooltipContent sideOffset={10} side='top' align='center' className='whitespace-pre-wrap'>
            {`${t('global.widget.ecologicalIntegrityIndex.tooltip.part1')}\n${t('global.widget.ecologicalIntegrityIndex.tooltip.list.item1')}\n${t('global.widget.ecologicalIntegrityIndex.tooltip.list.item2')}\n${t('global.widget.ecologicalIntegrityIndex.tooltip.list.item3')}\n${t('global.widget.ecologicalIntegrityIndex.tooltip.part2')}`}
          </TooltipContent>
        </Tooltip>
      ),
      cell: EcologicalIntegrityIndexCell,
    },
    {
      accessorKey: ProjectTypeAccessorKey.structuralIntegrityIndex,
      header: ({ column }: CellHeader) => (
        <Tooltip>
          <TooltipTrigger asChild className='uppercase'>
            <ToggleSortButton column={column} className='-ml-2.5'>
              {t('shared.ncaDetail.details.ecosystemIntegrityIndex.abbreviations.structuralIntegrity')}
            </ToggleSortButton>
          </TooltipTrigger>
          <TooltipContent sideOffset={10} side='top' align='center' className='whitespace-pre-wrap'>
            {t('global.widget.ecologicalIntegrityIndex.decomposition.structural_integrity_score.tooltip.body')}
          </TooltipContent>
        </Tooltip>
      ),
      cell: ({ row }: CellContext<PlotRow, unknown>) => (
        <FactCell plot={row.original} factType={IndicatorName.structural_integrity_score} unitType={UnitEnum.none} />
      ),
    },
    {
      accessorKey: ProjectTypeAccessorKey.compositionalIntegrityIndex,
      header: ({ column }: CellHeader) => (
        <Tooltip>
          <TooltipTrigger asChild className='uppercase'>
            <ToggleSortButton column={column} className='-ml-2.5'>
              {t('shared.ncaDetail.details.ecosystemIntegrityIndex.abbreviations.compositionalIntegrity')}
            </ToggleSortButton>
          </TooltipTrigger>
          <TooltipContent sideOffset={10} side='top' align='center' className='whitespace-pre-wrap'>
            {t('global.widget.ecologicalIntegrityIndex.decomposition.compositional_integrity_score.tooltip.body')}
          </TooltipContent>
        </Tooltip>
      ),
      cell: ({ row }: CellContext<PlotRow, unknown>) => (
        <FactCell plot={row.original} factType={IndicatorName.compositional_integrity_score} unitType={UnitEnum.none} />
      ),
    },
    {
      accessorKey: ProjectTypeAccessorKey.functionalIntegrityIndex,
      header: ({ column }: CellHeader) => (
        <Tooltip>
          <TooltipTrigger asChild className='uppercase'>
            <ToggleSortButton column={column} className='-ml-2.5'>
              {t('shared.ncaDetail.details.ecosystemIntegrityIndex.abbreviations.functionalIntegrity')}
            </ToggleSortButton>
          </TooltipTrigger>
          <TooltipContent sideOffset={10} side='top' align='center' className='whitespace-pre-wrap'>
            {t('global.widget.ecologicalIntegrityIndex.decomposition.functional_integrity_score.tooltip.body')}
          </TooltipContent>
        </Tooltip>
      ),
      cell: ({ row }: CellContext<PlotRow, unknown>) => (
        <FactCell plot={row.original} factType={IndicatorName.functional_integrity_score} unitType={UnitEnum.none} />
      ),
    },
    ...useBiodiversityCells(),
    ...useAGCarbonCells(),
    ...useBGCarbonCells(),
    ...useWaterCells(),
  ];
};

export const useBiodiversityCells = () => {
  const { t } = useTranslation();
  const hideTrendColumns = useHideTrendFacts();

  const biodiversityColumns = [
    {
      accessorKey: ProjectTypeAccessorKey.biodiversityZone,
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.plots.tableHeader.protectedHabitat')}
        </ToggleSortButton>
      ),
      cell: BiodiversityZoneCell,
    },
  ];

  if (!hideTrendColumns) {
    biodiversityColumns.push({
      accessorKey: ProjectTypeAccessorKey.biodiversityZoneTrend_1y,
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.ncaDetail.details.labels.oneYearChange')}
        </ToggleSortButton>
      ),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      cell: BiodiversityZoneTrendCell as unknown as any,
    });
  }

  return biodiversityColumns;
};

export const useHabitatIntactnessCells = () => {
  const { t } = useTranslation();
  const hideTrendColumns = useHideTrendFacts();

  // TODO: MVP-7361 Substitute isSubscribedToHabitatIntactness hooks logic by useHasHabitatIntactnessData() when available
  const purchasedIndicatorProducts =
    useIndicatorProduct({
      purchased: true,
      visible_for_customer: true,
    }).data ?? [];
  const isSubscribedToHabitatIntactness = !!purchasedIndicatorProducts.some((ip) =>
    ip.indicator_names.some((name: IndicatorFactType) =>
      ([IndicatorName.habitat_intactness_total] as IndicatorFactType[]).includes(name),
    ),
  );

  if (!isSubscribedToHabitatIntactness) {
    return [];
  }

  const hiColumns = [
    {
      accessorKey: ProjectTypeAccessorKey.habitatIntactness,
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.plots.tableHeader.habitatIntactness')}
        </ToggleSortButton>
      ),
      cell: ({ row }: CellContext<PlotRow, unknown>) => (
        <FactCell plot={row.original} factType={IndicatorName.habitat_intactness_total} unitType={UnitEnum['%']} />
      ),
    },
  ];

  if (!hideTrendColumns) {
    hiColumns.push({
      accessorKey: ProjectTypeAccessorKey.habitatIntactnessTrend_5y,
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.plots.tableHeader.habitatIntactnessTrend_5y')}
        </ToggleSortButton>
      ),
      cell: ({ row }: CellContext<PlotRow, unknown>) => (
        <FactTrendCell
          plot={row.original}
          factType={IndicatorName.habitat_intactness_total_previous_5y_pct_change}
          unitType={UnitEnum['%']}
        />
      ),
    });
  }

  return hiColumns;
};

export const useBGCarbonCells = () => {
  const { t } = useTranslation();
  const hideTrendColumns = useHideTrendFacts();

  const carbonColumns = [
    {
      accessorKey: ProjectTypeAccessorKey.carbonStorageBg,
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.plots.tableHeader.carbonStorageBG')}
        </ToggleSortButton>
      ),
      cell: CarbonStorageBgCell,
    },
  ];

  if (!hideTrendColumns) {
    carbonColumns.push({
      accessorKey: ProjectTypeAccessorKey.carbonStorageBgTrend_1y,
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.ncaDetail.details.labels.oneYearChange')}
        </ToggleSortButton>
      ),
      cell: CarbonStorageBgTrendCell,
    });
  }

  return carbonColumns;
};

export const useAGCarbonCells = () => {
  const { t } = useTranslation();
  const hideTrendColumns = useHideTrendFacts();
  const agcFacts = useAboveGroundCarbonFacts();
  if (isNullish(agcFacts)) {
    return [];
  }

  const carbonColumns = [
    {
      accessorKey: ProjectTypeAccessorKey.carbonStorageAg,
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.plots.tableHeader.carbonStorageAG')}
        </ToggleSortButton>
      ),
      cell: CarbonStorageAgCell,
    },
  ];

  if (!hideTrendColumns) {
    carbonColumns.push({
      accessorKey: ProjectTypeAccessorKey.carbonStorageAgTrend_1y,
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.ncaDetail.details.labels.oneYearChange')}
        </ToggleSortButton>
      ),
      cell: CarbonStorageAgTrendCell,
    });
  }

  return carbonColumns;
};

export const useWaterCells = () => {
  const { t } = useTranslation();
  const hideTrendColumns = useHideTrendFacts();

  const waterColumns = [
    {
      accessorKey: ProjectTypeAccessorKey.waterHoldingCapacity,
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.plots.tableHeader.waterHoldingCapacity')}
        </ToggleSortButton>
      ),
      cell: WaterHoldingCapacityCell,
    },
  ];

  if (!hideTrendColumns) {
    waterColumns.push({
      accessorKey: ProjectTypeAccessorKey.waterHoldingCapacityTrend_1y,
      header: ({ column }: CellHeader) => (
        <ToggleSortButton column={column} className='-ml-2.5'>
          {t('shared.ncaDetail.details.labels.oneYearChange')}
        </ToggleSortButton>
      ),
      cell: WaterHoldingCapacityTrendCell,
    });
  }

  return waterColumns;
};
